import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import type { DatasetSchemaColumn, StreamResource, TestStreamTransformResponse } from "@prequel/import-react";
import {
  useCreateStream,
  useDatasetSchema,
  useRandomSampleDataset,
  useTestExistingStream,
  useTestNewStream,
  useUpdateStream,
} from "@prequel/import-react";
import {
  Alert,
  AlertKind,
  Button,
  ButtonStyle,
  Dropdown,
  type DropdownListItem,
  FormField,
  SearchableDropdown,
  type SearchableDropdownListItem,
  Spinner,
  Toggle,
} from "@prequel/shared-components";

import ExpressionEditor from "./ExpressionEditor";
import {
  buildStreamOptions,
  buildUpdateStreamOptions,
  createEmptyMappingDraft,
  type DestinationField,
  getDatasetDisplayName,
  getDatasetProviderID,
  getDestinationFields,
  getFirstTransformError,
  getProviderDatasets,
  getProviderDestinations,
  getSampleRecordIDs,
  getTransformedDataPreview,
  isMappingDraftConfigured,
  mappingsToDrafts,
  type StreamMappingDraft,
} from "./helpers";

import { getErrorMessage } from "@/api/helpers";
import { useAllDatasets } from "@/api/import/datasets";
import { useAllDestinations } from "@/api/import/destinations";
import { useAllImportProducts } from "@/api/import/products";
import { useAllProviders } from "@/api/import/providers";
import { useAllSources } from "@/api/import/sources";
import ColumnCombobox, { type ImportColumnOption } from "@/components/import/ColumnCombobox";
import { Divider, FormFooter, FormSection, ReadOnlyFormValue } from "@/components/import/FormLayout";
import PreviewTable from "@/components/import/PreviewTable";
import { useToast } from "@/providers/ToastProvider";

type MappingSourceType = "field" | "expression";
type TestStatus = "success" | "error" | "processing" | undefined;

type StreamTestState = {
  status: TestStatus;
  message?: string;
  result?: TestStreamTransformResponse;
};

const SAMPLE_SIZE = 10;
const EMPTY_TEST_STATE: StreamTestState = { status: undefined };
const EMPTY_DATASET_COLUMNS: DatasetSchemaColumn[] = [];
const SOURCE_TYPE_ITEMS: DropdownListItem<MappingSourceType>[] = [
  { key: "field", text: "Column" },
  { key: "expression", text: "Expression" },
];

type StreamFormProps = {
  streamId?: string;
  stream?: StreamResource;
  cancelPath?: string;
  onSaved?: () => void;
};

const StreamForm = ({ streamId, stream: existingStream, cancelPath = "/import/streams", onSaved }: StreamFormProps) => {
  const navigate = useNavigate();
  const isEditing = !!streamId;
  const formRef = useRef<HTMLFormElement>(null);
  const { addToast } = useToast();
  const providersQuery = useAllProviders();
  const productsQuery = useAllImportProducts();
  const sourcesQuery = useAllSources();
  const datasetsQuery = useAllDatasets();
  const destinationsQuery = useAllDestinations();
  const createStream = useCreateStream();
  const updateStream = useUpdateStream();
  const randomSampleDataset = useRandomSampleDataset();
  const testNewStream = useTestNewStream();
  const testExistingStream = useTestExistingStream();

  const [providerID, setProviderID] = useState("");
  const [streamName, setStreamName] = useState(existingStream?.name ?? "");
  const [datasetID, setDatasetID] = useState(existingStream?.dataset_id ?? "");
  const [destinationID, setDestinationID] = useState(existingStream?.destination_id ?? "");
  const [isEnabled, setIsEnabled] = useState(existingStream?.is_enabled ?? false);
  const [mappingsByTarget, setMappingsByTarget] = useState<Record<string, StreamMappingDraft>>(() =>
    mappingsToDrafts(existingStream?.mappings),
  );
  const [editingExpressionTarget, setEditingExpressionTarget] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [testState, setTestState] = useState<StreamTestState>(EMPTY_TEST_STATE);
  const [testedStreamFingerprint, setTestedStreamFingerprint] = useState<string>();
  const schemaErrorToastDatasetID = useRef<string | undefined>(undefined);

  const providers = providersQuery.items;
  const products = productsQuery.items;
  const sources = sourcesQuery.items;
  const datasets = datasetsQuery.items;
  const destinations = destinationsQuery.items;
  const selectedProvider = providers.find((provider) => provider.id === providerID);
  const providerDatasets = useMemo(
    () => getProviderDatasets(providerID, datasets, sources),
    [providerID, datasets, sources],
  );
  const providerDestinations = useMemo(
    () => getProviderDestinations(selectedProvider, products, destinations),
    [selectedProvider, products, destinations],
  );
  // A stream's dataset and destination are fixed, so editing looks them up directly instead of through the provider.
  const selectedDestination = (isEditing ? destinations : providerDestinations).find(
    (destination) => destination.id === destinationID,
  );
  const destinationFields = useMemo(
    () => getDestinationFields(selectedDestination?.record_schema),
    [selectedDestination?.record_schema],
  );
  const datasetSchema = useDatasetSchema(datasetID || undefined);
  const datasetColumns = datasetSchema.schema ?? EMPTY_DATASET_COLUMNS;
  const requiredUnmappedFields = destinationFields.filter(
    (field) => field.required && !isMappingDraftConfigured(mappingsByTarget[field.name]),
  );
  const hasStreamName = streamName.trim().length > 0;
  const canSelectDataset = isEditing || providerID.length > 0;
  const canSelectDestination = isEditing || providerID.length > 0;
  const canConfigureMappings =
    canSelectDataset && datasetID.length > 0 && canSelectDestination && destinationID.length > 0;
  const isLoadingMappingInputs = datasetSchema.isLoading || (isEditing && destinationsQuery.isLoading);
  const isMutating =
    createStream.isCreating ||
    updateStream.isUpdating ||
    randomSampleDataset.isSampling ||
    testNewStream.isTesting ||
    testExistingStream.isTesting;

  const streamOptions = useMemo(
    () =>
      buildStreamOptions({
        name: streamName,
        datasetID,
        destinationID,
        mappingsByTarget,
      }),
    [streamName, datasetID, destinationID, mappingsByTarget],
  );
  // Only the mappings affect the transform, so renaming or toggling does not invalidate a passing test.
  const transformPatch = useMemo(() => buildUpdateStreamOptions({ mappingsByTarget }), [mappingsByTarget]);
  const streamFingerprint = useMemo(
    () => JSON.stringify(isEditing ? transformPatch : streamOptions),
    [isEditing, transformPatch, streamOptions],
  );
  const currentTestState = testedStreamFingerprint === streamFingerprint ? testState : EMPTY_TEST_STATE;
  const canTestMapping =
    canConfigureMappings &&
    hasStreamName &&
    destinationFields.length > 0 &&
    requiredUnmappedFields.length === 0 &&
    !isLoadingMappingInputs &&
    !isMutating;
  const canSave = currentTestState.status === "success" && !isMutating;

  const providerItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      providers.map((provider) => ({
        key: provider.id,
        text: `${provider.name} (${provider.id})`,
        searchValue: `${provider.name} ${provider.id}`,
      })),
    [providers],
  );
  const datasetItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      providerDatasets.map((dataset) => ({
        key: dataset.id,
        text: getDatasetDisplayName(dataset),
        searchValue: `${getDatasetDisplayName(dataset)} ${dataset.id}`,
      })),
    [providerDatasets],
  );
  const destinationItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      providerDestinations.map((destination) => ({
        key: destination.id,
        text: `${destination.name} (${destination.id})`,
        searchValue: `${destination.name} ${destination.id}`,
      })),
    [providerDestinations],
  );
  const editedProviderID = getDatasetProviderID(datasetID, datasets);
  const editedProvider = providers.find((provider) => provider.id === editedProviderID);
  const editedDataset = datasets.find((dataset) => dataset.id === datasetID);
  const providerDisplay = editedProvider ? `${editedProvider.name} (${editedProvider.id})` : editedProviderID;
  const datasetDisplay = editedDataset ? getDatasetDisplayName(editedDataset) : datasetID;
  const destinationDisplay = selectedDestination
    ? `${selectedDestination.name} (${selectedDestination.id})`
    : destinationID;
  const columnItems = useMemo<ImportColumnOption[]>(
    () =>
      datasetColumns.map((column) => ({
        name: column.column_name,
        prequelType: column.prequel_type,
        rawType: column.source_type,
      })),
    [datasetColumns],
  );

  useEffect(() => {
    if (!datasetID || !datasetSchema.error || schemaErrorToastDatasetID.current === datasetID) {
      return;
    }

    schemaErrorToastDatasetID.current = datasetID;
    addToast({
      kind: "ERROR",
      message: `Failed to load dataset schema: ${getErrorMessage(datasetSchema.error, "An unknown error occurred.")}`,
    });
  }, [addToast, datasetID, datasetSchema.error]);

  const resetDownstreamSelections = () => {
    setDatasetID("");
    setDestinationID("");
    setMappingsByTarget({});
    setEditingExpressionTarget(undefined);
    setFormError(undefined);
    schemaErrorToastDatasetID.current = undefined;
  };

  const onProviderSelected = (item: DropdownListItem<string>) => {
    setProviderID(item.key);
    resetDownstreamSelections();
  };

  const onDatasetSelected = (item: DropdownListItem<string>) => {
    setDatasetID(item.key);
    setMappingsByTarget({});
    setEditingExpressionTarget(undefined);
    setFormError(undefined);
    schemaErrorToastDatasetID.current = undefined;
  };

  const onDestinationSelected = (item: DropdownListItem<string>) => {
    setDestinationID(item.key);
    setMappingsByTarget({});
    setEditingExpressionTarget(undefined);
    setFormError(undefined);
  };

  const setTargetColumn = (targetField: string, columnName: string) => {
    setMappingsByTarget((currentMappings) => ({
      ...currentMappings,
      [targetField]: {
        ...(currentMappings[targetField] ?? createEmptyMappingDraft()),
        fieldType: "field",
        columnName,
      },
    }));
    setEditingExpressionTarget(undefined);
    setFormError(undefined);
  };

  const setTargetSourceType = (targetField: string, fieldType: MappingSourceType) => {
    setMappingsByTarget((currentMappings) => ({
      ...currentMappings,
      [targetField]: {
        ...(currentMappings[targetField] ?? createEmptyMappingDraft()),
        fieldType,
      },
    }));
    setEditingExpressionTarget(fieldType === "expression" ? targetField : undefined);
    setFormError(undefined);
  };

  const setTargetExpression = (targetField: string, expression: string) => {
    setMappingsByTarget((currentMappings) => ({
      ...currentMappings,
      [targetField]: {
        ...(currentMappings[targetField] ?? createEmptyMappingDraft()),
        fieldType: "expression",
        expression,
      },
    }));
    setEditingExpressionTarget(undefined);
    setFormError(undefined);
  };

  const validateForm = () => {
    if (!formRef.current?.reportValidity()) {
      return false;
    }

    if (!canConfigureMappings) {
      setFormError("Select a provider, dataset, and destination.");
      return false;
    }

    if (destinationFields.length === 0) {
      setFormError("The selected destination does not expose top-level schema fields.");
      return false;
    }

    if (requiredUnmappedFields.length > 0) {
      setFormError(`Map required destination fields: ${requiredUnmappedFields.map((field) => field.name).join(", ")}.`);
      return false;
    }

    setFormError(undefined);
    return true;
  };

  const testMapping = async () => {
    if (!validateForm()) {
      return;
    }

    setTestedStreamFingerprint(streamFingerprint);
    setTestState({ status: "processing", message: "Testing mapping..." });

    try {
      const sample = await randomSampleDataset.randomSampleDataset({ datasetId: datasetID, sampleSize: SAMPLE_SIZE });
      const recordIDs = getSampleRecordIDs(sample.records);
      if (recordIDs.length === 0) {
        setTestState({
          status: "error",
          message: "The sample endpoint did not return a record_id to test.",
        });
        return;
      }

      const result =
        isEditing && streamId
          ? await testExistingStream.testExistingStream(streamId, {
              stream: transformPatch,
              record_ids: recordIDs,
            })
          : await testNewStream.testNewStream({ stream: streamOptions, record_ids: recordIDs });
      const preview = getTransformedDataPreview(result, destinationFields);
      const transformError = getFirstTransformError(result);
      if (transformError) {
        setTestState({
          status: "error",
          message: transformError,
          result: preview,
        });
        return;
      }

      setTestState({
        status: "success",
        message: "Mapping test succeeded.",
        result: preview,
      });
    } catch (error) {
      setTestState({ status: "error", message: getErrorMessage(error) });
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    if (currentTestState.status !== "success") {
      setFormError("Test the current mapping before saving.");
      return;
    }

    if (isEditing && streamId) {
      try {
        await updateStream.updateStream(
          streamId,
          buildUpdateStreamOptions({ name: streamName, isEnabled, mappingsByTarget }),
        );
        addToast({ kind: "SUCCESS", message: "Stream updated successfully!" });
        onSaved?.();
        navigate(cancelPath);
      } catch (error) {
        addToast({ kind: "ERROR", message: getErrorMessage(error, "Stream update failed.") });
      }
      return;
    }

    try {
      const stream = await createStream.createStream(streamOptions);
      addToast({ kind: "SUCCESS", message: "Stream created successfully!" });
      navigate(`/import/streams/${stream.id}`);
    } catch (error) {
      addToast({ kind: "ERROR", message: getErrorMessage(error, "Stream creation failed.") });
    }
  };

  if (providersQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <form className="space-y-8 pb-16" onSubmit={onSubmit} ref={formRef}>
      {!!providersQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load providers.</Alert>}
      {!!productsQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load products.</Alert>}
      {!!sourcesQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load sources.</Alert>}
      {!!datasetsQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load datasets.</Alert>}
      {!!destinationsQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load destinations.</Alert>}
      {formError && <Alert kind={AlertKind.ERROR}>{formError}</Alert>}

      <FormSection title="Select a Provider">
        <div className="space-y-6">
          {isEditing ? (
            <ReadOnlyFormValue label="Provider ID" value={providerDisplay} />
          ) : (
            <SearchableDropdown
              label="Provider ID"
              items={providerItems}
              selectedItem={providerItems.find((item) => item.key === providerID)}
              setSelectedItem={onProviderSelected}
              isLoading={providersQuery.isLoading}
              loadingText="Loading providers..."
              emptyText="No providers are available."
              disabled={isMutating}
              subtext="Provider name (id)"
            />
          )}
        </div>
      </FormSection>

      <Divider />

      <FormSection title="Configure Stream mapping">
        <div className="space-y-6">
          <FormField
            id="stream-name"
            label="Name"
            type="text"
            value={streamName}
            onChangeHandler={setStreamName}
            required
            disabled={isMutating}
            subtext="A descriptive name for this stream."
          />
          {isEditing ? (
            <>
              <ReadOnlyFormValue label="Dataset" value={datasetDisplay} />
              <ReadOnlyFormValue label="Destination" value={destinationDisplay} />
              <Toggle
                label="Enabled"
                checked={isEnabled}
                setChecked={setIsEnabled}
                disabled={isMutating}
                subtext="When enabled, this stream loads records into its destination."
              />
            </>
          ) : (
            <>
              <SearchableDropdown
                label="Dataset"
                items={datasetItems}
                selectedItem={datasetItems.find((item) => item.key === datasetID)}
                setSelectedItem={onDatasetSelected}
                isLoading={canSelectDataset && (datasetsQuery.isLoading || sourcesQuery.isLoading)}
                loadingText="Loading datasets..."
                emptyText={canSelectDataset ? "No datasets are available for this provider." : undefined}
                disabled={!canSelectDataset || isMutating || datasetItems.length === 0}
                subtext="The dataset this stream reads records from."
              />
              <SearchableDropdown
                label="Destination"
                items={destinationItems}
                selectedItem={destinationItems.find((item) => item.key === destinationID)}
                setSelectedItem={onDestinationSelected}
                isLoading={canSelectDestination && (destinationsQuery.isLoading || productsQuery.isLoading)}
                loadingText="Loading destinations..."
                emptyText={canSelectDestination ? "No destinations are available for this provider." : undefined}
                disabled={!canSelectDestination || isMutating || destinationItems.length === 0}
                subtext="Where transformed records are sent."
              />
            </>
          )}

          {canConfigureMappings ? (
            <div className="space-y-6">
              <div className="space-y-1">
                <h3 className="text-base font-semibold text-gray-900">Field Mappings</h3>
                <p className="max-w-3xl text-sm text-gray-500">
                  Map the dataset fields into the target destination schema. Choose a source column or write an
                  expression for each mapping.
                </p>
              </div>

              {isLoadingMappingInputs ? (
                <Spinner />
              ) : (
                <FieldMappingsTable
                  fields={destinationFields}
                  datasetID={datasetID}
                  columnItems={columnItems}
                  mappingsByTarget={mappingsByTarget}
                  setTargetColumn={setTargetColumn}
                  setTargetExpression={setTargetExpression}
                  setTargetSourceType={setTargetSourceType}
                  editingExpressionTarget={editingExpressionTarget}
                  setEditingExpressionTarget={setEditingExpressionTarget}
                  disabled={isMutating}
                />
              )}
            </div>
          ) : (
            <Alert kind={AlertKind.INFO}>Select a dataset and destination to configure field mappings.</Alert>
          )}
        </div>
      </FormSection>

      {canConfigureMappings && (
        <>
          <Divider />

          <FormSection title="Testing & Submission">
            <div className="space-y-6">
              <TestMappingCallout />
              <TestResult state={currentTestState} />
              <FormFooter justify="end">
                <Button
                  type={ButtonStyle.TERTIARY}
                  text="Cancel"
                  onClick={() => navigate(cancelPath)}
                  disabled={isMutating}
                />
                <TestMappingActions
                  disabled={!canTestMapping}
                  isTesting={currentTestState.status === "processing"}
                  onTestMapping={testMapping}
                />
                <Button
                  type={ButtonStyle.PRIMARY}
                  disabled={!canSave}
                  submit
                  text={
                    createStream.isCreating || updateStream.isUpdating ? (
                      <div className="flex">
                        <Spinner.Inline className="mr-3 text-white" />
                        {isEditing ? "Updating Stream..." : "Saving Stream..."}
                      </div>
                    ) : isEditing ? (
                      "Update Stream"
                    ) : (
                      "Save Stream"
                    )
                  }
                />
              </FormFooter>
            </div>
          </FormSection>
        </>
      )}
    </form>
  );
};

const FieldMappingsTable = ({
  fields,
  datasetID,
  columnItems,
  mappingsByTarget,
  setTargetColumn,
  setTargetExpression,
  setTargetSourceType,
  editingExpressionTarget,
  setEditingExpressionTarget,
  disabled,
}: {
  fields: DestinationField[];
  datasetID: string;
  columnItems: ImportColumnOption[];
  mappingsByTarget: Record<string, StreamMappingDraft>;
  setTargetColumn: (targetField: string, columnName: string) => void;
  setTargetExpression: (targetField: string, expression: string) => void;
  setTargetSourceType: (targetField: string, fieldType: MappingSourceType) => void;
  editingExpressionTarget?: string;
  setEditingExpressionTarget: (targetField: string | undefined) => void;
  disabled: boolean;
}) => {
  if (fields.length === 0) {
    return <Alert kind={AlertKind.INFO}>The selected destination has no top-level schema fields.</Alert>;
  }

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
      {fields.map((field) => {
        const mapping = mappingsByTarget[field.name] ?? createEmptyMappingDraft();
        const selectedType = SOURCE_TYPE_ITEMS.find((item) => item.key === mapping.fieldType);
        const isEditingExpression = editingExpressionTarget === field.name;

        return (
          <div key={field.name}>
            <div
              data-testid={`stream-mapping-row-${field.name}`}
              className="
                grid items-start gap-4 border-b border-gray-200 p-4
                last:border-b-0
                md:grid-cols-[10rem_minmax(0,1fr)_2rem_minmax(0,1fr)]
              "
            >
              <Dropdown
                label="Type"
                items={SOURCE_TYPE_ITEMS}
                selectedItem={selectedType}
                setSelectedItem={(item) => setTargetSourceType(field.name, item.key)}
                disabled={disabled}
              />
              {mapping.fieldType === "expression" ? (
                <ExpressionMappingControl
                  expression={mapping.expression}
                  disabled={disabled}
                  required={field.required}
                  onOpen={() => setEditingExpressionTarget(field.name)}
                />
              ) : (
                <ColumnCombobox
                  label="Dataset Column or Expression"
                  columns={columnItems}
                  selectedColumn={mapping.columnName}
                  setSelectedColumn={(columnName) => setTargetColumn(field.name, columnName)}
                  disabled={disabled || columnItems.length === 0}
                  // Source schemas can be partial, so stream mappings allow manually typed source columns.
                  allowCustomValue
                  helperText={field.required ? "Required destination field." : undefined}
                />
              )}
              <div
                className="
                  hidden h-10 items-center justify-center pt-6 text-lg text-gray-400
                  md:flex
                "
                aria-hidden
              >
                &rarr;
              </div>
              <DestinationFieldReadout field={field} />
            </div>
            {isEditingExpression && (
              <ExpressionEditor
                datasetID={datasetID}
                field={field}
                initialExpression={mapping.expression}
                onCancel={() => setEditingExpressionTarget(undefined)}
                onInsert={(expression) => setTargetExpression(field.name, expression)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const ExpressionMappingControl = ({
  expression,
  disabled,
  required,
  onOpen,
}: {
  expression: string;
  disabled: boolean;
  required: boolean;
  onOpen: () => void;
}) => (
  <div className="min-w-0">
    <label className="mb-1 block text-sm font-medium text-gray-700">Dataset Column or Expression</label>
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      className="
        flex min-h-10 w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm
        hover:bg-gray-50
        disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
      "
    >
      {expression ? (
        <code className="min-w-0 truncate font-mono text-sm text-gray-900">{expression}</code>
      ) : (
        <span className="text-sm text-gray-400">Write expression...</span>
      )}
    </button>
    {required && <p className="mt-2 text-xs text-gray-400">Required destination field.</p>}
  </div>
);

const DestinationFieldReadout = ({ field }: { field: DestinationField }) => (
  <div className="min-w-0">
    <label className="mb-1 block text-sm font-medium text-gray-700">Destination Field</label>
    <div className="min-h-10 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium text-gray-900">{field.name}</span>
        <span
          className={`
            text-xs font-medium
            ${field.required ? "text-red-600" : "text-gray-500"}
          `}
        >
          {field.required ? "Required" : "Optional"}
        </span>
        <span className="rounded-sm bg-white px-1.5 py-0.5 text-xs text-gray-600 ring-1 ring-gray-200">
          {field.type}
        </span>
      </div>
    </div>
  </div>
);

const TestMappingCallout = () => (
  <Alert kind={AlertKind.INFO}>
    Test the mapping before saving. Test Mapping will apply your configured transformation and show the output. This
    test will not stream any records to the destination.
  </Alert>
);

const TestResult = ({ state }: { state: StreamTestState }) => {
  if (!state.status) {
    return null;
  }

  if (state.status === "processing") {
    return (
      <div className="flex items-center rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
        <Spinner.Inline className="mr-3 text-gray-500" />
        {state.message}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Alert kind={state.status === "success" ? AlertKind.SUCCESS : AlertKind.ERROR}>{state.message}</Alert>
      {state.result && <PreviewTable preview={state.result} />}
    </div>
  );
};

const TestMappingActions = ({
  disabled,
  isTesting,
  onTestMapping,
}: {
  disabled: boolean;
  isTesting: boolean;
  onTestMapping: () => void;
}) => (
  <button
    type="button"
    onClick={onTestMapping}
    disabled={disabled}
    className="
      inline-flex items-center justify-center rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white
      transition-colors
      hover:bg-emerald-600
      focus:z-10 focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 focus:outline-none
      disabled:cursor-not-allowed disabled:bg-emerald-500 disabled:opacity-90
    "
  >
    {isTesting ? (
      <>
        <Spinner.Inline className="mr-3 text-white" />
        Testing Mapping...
      </>
    ) : (
      "Test Mapping"
    )}
  </button>
);

export default StreamForm;
